import React, { useEffect, useState } from "react";
import { Snackbar, Alert, Button, Tooltip } from "@mui/material";
import { useOutletContext, useNavigate } from "react-router-dom";
import { TablePagination } from "@mui/material";
import { getFlexpertRequests, updateConsultantToFlexpert } from "./Helper";
import countryData from "country-calling-code";
import { useDebounce } from "../../../hooks";
import MuiModal from "../../../components/MuiModal";
import { ThemeSelect } from "../../../components/common";

const baseFeUrl = process.env.REACT_APP_BASE_FE_URL || "";

const FlexpertRequestsList = () => {
  const context = useOutletContext();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const debounceSearch = useDebounce(search, 1000);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [userPermissions, setUserPermissions] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [userToApprove, setUserToApprove] = useState(null);
  const [isApproving, setIsApproving] = useState(false);
  const [countryFilter, setCountryFilter] = useState("");

  const fetchData = (pageNum, pageSize) => {
    const countryParam = countryFilter || null;
    getFlexpertRequests(pageNum, pageSize, debounceSearch, countryParam)
      .then((data) => {
        setRequests(data.data?.consultants?.rows || []);
        setTotal(data.data?.consultants?.totalItems || 0);
      })
      .catch((err) => console.log(err));
  };

  const handleChangePage = (event, newPage) => {
    fetchData(newPage, limit);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setPage(0);
    fetchData(0, newLimit);
  };

  useEffect(() => {
    context(["Flexperts", "Flexperts requests"]);
    fetchData(page, limit);
  }, [debounceSearch, countryFilter]);

  useEffect(() => {
    const cmsPermissions = localStorage.getItem("CMSUsersPermission");
    if (cmsPermissions) {
      const permissions = JSON.parse(cmsPermissions);
      setUserPermissions(permissions);
      setIsAdmin(false);
      if (!permissions.includes("user-manager")) {
        navigate("/Dashboard");
      }
    } else {
      setUserPermissions([]);
      setIsAdmin(true);
    }
  }, [navigate]);

  const handleApproveClick = (row) => {
    setUserToApprove(row);
    setApproveModalOpen(true);
  };

  const handleConfirmApprove = () => {
    if (!userToApprove) return;
    setIsApproving(true);
    updateConsultantToFlexpert(userToApprove.id, true, null, null, null)
      .then(() => {
        setSnackbarMessage("User approved as Flexpert successfully.");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setApproveModalOpen(false);
        setUserToApprove(null);
        fetchData(page, limit);
      })
      .catch((err) => {
        const errorMessage =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to approve user.";
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      })
      .finally(() => setIsApproving(false));
  };

  const formatPhone = (row) => {
    if (row?.countryCode && row?.phone) {
      return `${row.countryCode.startsWith("+") ? "" : "+"}${row.countryCode}${row.phone}`;
    }
    return row?.phone ? row.phone : "--";
  };

  const formatCountry = (row) => {
    if (row?.country) return row.country;
    if (row?.countryCode) {
      const found = countryData.find((c) =>
        c.countryCodes.includes(row.countryCode)
      );
      return found?.country || "--";
    }
    return "--";
  };

  const currencySymbol = (code) => {
    const map = { INR: "₹", USD: "$", EUR: "€", GBP: "£" };
    return map[code] || code || "";
  };

  const SalaryCell = ({ salaryDetails }) => {
    if (!salaryDetails?.length) return <span className="text-gray-400">--</span>;
    return (
      <div className="text-left space-y-1">
        {salaryDetails.map((s, i) => (
          <div key={i} className="text-sm">
            <span className="font-semibold text-gray-600 text-xs">{s.type}:</span>{" "}
            <span className="text-gray-700 font-semibold">{currencySymbol(s.currency)}</span>
            <span className="text-gray-900 font-medium"> {s.min}–{s.max}</span>
          </div>
        ))}
      </div>
    );
  };

  const QATooltipContent = ({ screeningAnswers }) => {
    if (!screeningAnswers?.length) return <span>No answers</span>;
    return (
      <div className="min-w-[380px] max-w-[480px] bg-white rounded-md shadow-lg border border-gray-200 p-2.5">
        <div className="space-y-2">
          {screeningAnswers.map((qa, i) => (
            <div key={i} className="border-b border-gray-200 pb-2 last:border-0 last:pb-0">
              <div className="flex gap-1.5 mb-1">
                <span className="font-semibold text-[#2EBAA3] text-xs flex-shrink-0">Q:</span>
                <span className="text-gray-800 text-xs font-medium leading-tight">{qa.question}</span>
              </div>
              <div className="flex gap-1.5">
                <span className="font-semibold text-gray-500 text-xs flex-shrink-0">A:</span>
                <span className="text-gray-600 text-xs leading-tight">{qa.answer}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="p-5 shadow-md bg-white">
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {approveModalOpen && userToApprove && (
        <MuiModal
          open={approveModalOpen}
          onCancel={() => {
            if (!isApproving) {
              setApproveModalOpen(false);
              setUserToApprove(null);
            }
          }}
          title="Approve Flexpert"
        >
          <div className="flex flex-col">
            <span className="text-base">
              Are you sure you want to approve this user as Flexpert?
            </span>
            {userToApprove && (
              <span className="text-sm text-gray-600 mt-2">
                User: {userToApprove.firstName} {userToApprove.lastName} (
                {userToApprove.email})
              </span>
            )}
            <div className="flex justify-center gap-4 pt-4">
              <button
                className="bg-[#2ebaa3] hover:bg-[#259a85] text-white py-2 px-4 rounded-lg disabled:opacity-50"
                onClick={handleConfirmApprove}
                disabled={isApproving}
              >
                {isApproving ? "Approving..." : "Approve"}
              </button>
              <button
                className="bg-red-400 hover:bg-red-500 text-white py-2 px-4 rounded-lg"
                onClick={() => {
                  setApproveModalOpen(false);
                  setUserToApprove(null);
                }}
                disabled={isApproving}
              >
                Cancel
              </button>
            </div>
          </div>
        </MuiModal>
      )}

      <div className="grid grid-cols-6 gap-2 items-center">
        <h3
          className="col-span-3 md:col-span-2 text-lg uppercase tracking-wider font-bold"
          style={{ color: "#2EBAA3" }}
        >
          Flexperts requests
        </h3>
        <div className="col-span-3 md:col-span-4 flex justify-end items-center gap-2">
          <ThemeSelect
            options={[
              { value: "", label: "All countries" },
              { value: "INDIA", label: "India" },
              { value: "OTHERS", label: "Others" },
            ]}
            value={countryFilter}
            onChange={(val) => {
              setCountryFilter(val);
              setPage(0);
            }}
            placeholder="All countries"
          />
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-1.5 form-control block max-w-xs text-base font-normal text-gray-700 bg-white bg-clip-padding border border-solid border-gray-300 rounded transition ease-in-out focus:text-gray-700 focus:bg-white focus:outline-none"
          />
        </div>
      </div>
      <br />
      <div className="overflow-x-auto relative">
        <table className="w-max min-w-full text-sm text-left text-gray-500 table-auto">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50">
            <tr>
              <th scope="col" className="py-3 px-6 uppercase">
                Sr.
              </th>
              <th scope="col" className="py-3 px-6 uppercase">
                Name
              </th>
              <th scope="col" className="py-3 px-6 uppercase">
                Profile ID
              </th>
              <th scope="col" className="py-3 px-6 uppercase">
                Email
              </th>
              <th scope="col" className="py-3 px-6 uppercase">
                Phone no.
              </th>
              <th scope="col" className="py-3 px-6 uppercase">
                Country
              </th>
              <th scope="col" className="py-3 px-6 uppercase min-w-[180px]">
                Salary details
              </th>
              <th scope="col" className="py-3 px-6 uppercase">
                Screening Q&A
              </th>
              <th scope="col" className="py-3 px-6 uppercase">
                Is Approved
              </th>
            </tr>
          </thead>
          <tbody>
            {requests.map((row, index) => (
              <tr className="bg-white border-b" key={row.id || index}>
                <th
                  scope="row"
                  className="py-4 px-6 font-medium text-gray-900 whitespace-nowrap"
                >
                  {page * limit + index + 1}
                </th>
                <td className="py-4 px-6">
                  {row?.firstName} {row?.lastName}
                </td>
                <td className="py-4 px-6">
                  {row?.username ? (
                    <a
                      href={`${baseFeUrl}/${row.username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#2EBAA3] hover:underline cursor-pointer font-medium"
                    >
                      {row.username}
                    </a>
                  ) : (
                    <span className="text-gray-400">--</span>
                  )}
                </td>
                <td className="py-4 px-6">{row?.email || "--"}</td>
                <td className="py-4 px-6">{formatPhone(row)}</td>
                <td className="py-4 px-6">{formatCountry(row)}</td>
                <td className="py-4 px-6 min-w-[180px]">
                  <SalaryCell salaryDetails={row?.salaryDetails} />
                </td>
                <td className="py-4 px-6">
                  {row?.flexpertScreeningAnswers?.length ? (
                    <Tooltip
                      title={
                        <QATooltipContent
                          screeningAnswers={row.flexpertScreeningAnswers}
                        />
                      }
                      arrow
                      placement="left-end"
                      leaveDelay={0}
                      componentsProps={{
                        popper: {
                          sx: {
                            "& .MuiTooltip-tooltip": {
                              backgroundColor: "transparent !important",
                              padding: 0,
                              maxWidth: "none",
                              boxShadow: "none",
                            },
                            "& .MuiTooltip-arrow": {
                              color: "#ffffff",
                              "&::before": {
                                border: "1px solid #e5e7eb",
                              },
                            },
                          },
                        },
                      }}
                      TransitionProps={{ timeout: { exit: 100 } }}
                    >
                      <span className="text-[#2EBAA3] no-underline hover:underline cursor-pointer">
                        View answers
                      </span>
                    </Tooltip>
                  ) : (
                    <span className="text-gray-400">--</span>
                  )}
                </td>
                <td className="py-4 px-6">
                  {row?.flexpertApprovedByAdmin === true ? (
                    <span className="text-green-600 font-medium">Approved</span>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleApproveClick(row)}
                      sx={{
                        backgroundColor: "#2EBAA3",
                        "&:hover": { backgroundColor: "#259a85" },
                        color: "white",
                      }}
                    >
                      Approve
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={limit}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[2, 5, 10, 25, 50, 100]}
      />
    </div>
  );
};

export default FlexpertRequestsList;
